import React, { useCallback, useMemo, useState } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { askPortfolioQuestionStarted, PortfolioAnalysisPage, getPortfolioAnalyticsTemplates, setSurveyIndex, setPortfolioQuestion, fetchDocumentFieldsStarted, setSurveySummary, PortfolioAnalysisTemplate, setPortfolioAnalyticsTemplate, getIsFetchingTemplates, setPortfolioAnalysisPageView } from './store';
import styles from './PortfolioAnalytics.module.scss';
import { Button } from '../../shared/button/Button';
import { getAllClientDatasets } from '../client-datasets/store';
import { getAllDocumentNames } from '../documents/store';
import { formatDate } from '../../../utils/luxon';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { Spinner } from '../../shared/spinner/Spinner';
import { OverflowTooltip, TooltipList } from '../../shared/tooltip';
import { PortfolioAnalyticsTemplateDeleteModal } from './PortfolioAnalyticsTemplateDeleteModal';
import { getBaseEntityDropdownOptions } from '../entity/store';
import { PreExecutionFilterOption } from '../../documents/search/store';

interface PortfolioAnalyticsSurveyProps {
    changePageView: (view: PortfolioAnalysisPage) => void;
}

export const PortfolioAnalyticsTemplates: React.FC<PortfolioAnalyticsSurveyProps> = ({ changePageView }) => {

    const [templateIdToDelete, setTemplateIdToDelete] = useState<number | null>(null);

    const datasets = useAppSelector(getAllClientDatasets);
    const documentNames = useAppSelector(getAllDocumentNames);
    const isFetchingTemplates = useAppSelector(getIsFetchingTemplates);
    const entityOptions = useAppSelector(getBaseEntityDropdownOptions);

    const getSelectedDataset = useCallback((parentDatasetId: number) => datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '', [datasets]);
    const getDocumentNames = useCallback((parentDatasetId: number, documentNameIds: number[]) => documentNames.filter(({ datasetId, documentNameId }) => datasetId === parentDatasetId && documentNameId && documentNameIds.includes(documentNameId)).map(({ documentName }) => documentName), [documentNames]);

    const dispatch = useAppDispatch();
    const templates = useAppSelector(getPortfolioAnalyticsTemplates);

    const editTemplate = useCallback((template: PortfolioAnalysisTemplate) => {
        const { metaData } = template;
        dispatch(setPortfolioQuestion(metaData));
        dispatch(fetchDocumentFieldsStarted(metaData.parentDatasetId!.toString()));
        dispatch(setSurveyIndex(2));
        changePageView(PortfolioAnalysisPage.SURVEY);
        dispatch(setPortfolioAnalyticsTemplate(template));
    }, [dispatch, changePageView]);

    const openDeleteTemplateModal = useCallback((portfolioAnalysisTemplateId: number) => setTemplateIdToDelete(portfolioAnalysisTemplateId), []);

    const askAgain = useCallback((template: PortfolioAnalysisTemplate) => {
        const { metaData } = template;
        const { parentDatasetId, question, fieldLabel, filters: { documentNameIds } } = metaData;
        const agreementType = getSelectedDataset(parentDatasetId!);
        const documentNames = getDocumentNames(parentDatasetId!, documentNameIds);
        dispatch(setPortfolioQuestion(metaData));
        dispatch(fetchDocumentFieldsStarted(metaData.parentDatasetId!.toString()));
        dispatch(askPortfolioQuestionStarted(metaData));
        dispatch(setSurveySummary({ agreementType, documentNames, fieldLabel, questionAsked: question }));
        changePageView(PortfolioAnalysisPage.RESULTS);
        dispatch(setPortfolioAnalyticsTemplate(template));
    }, [dispatch, getDocumentNames, getSelectedDataset, changePageView]);

    const entityFilter = useCallback((entityIds: string[] | undefined) => {
        const getEntityName = (val: string) => entityOptions.find(({ value }) => value === val)?.label || '';
        if (isNull(entityIds) || isUndefined(entityIds)) {
            return [];
        }
        return entityIds.map(value => getEntityName(value));
    }, [entityOptions]);

    const preExecutionFilter = useCallback((includePreExecution: PreExecutionFilterOption | undefined) => {
        if (isUndefined(includePreExecution) || includePreExecution === PreExecutionFilterOption.EXCLUDE) {
            return null;
        }
        if (includePreExecution === PreExecutionFilterOption.EXCLUSIVE) {
            return 'Exclusively Pre-Execution documents';
        }
        return 'Include Pre-Execution documents';
    }, []);

    const changeView = useCallback(() => dispatch(setPortfolioAnalysisPageView(PortfolioAnalysisPage.SELECT)), [dispatch]);

    const body = useMemo(() => {
        if (isFetchingTemplates) {
            return <Spinner />;
        }
        if (templates.length === 0) {
            return <div className={styles.noTemplatesWrapper}>You do not currently have any templates, please complete the survey and click save on the results page to add a new template.</div>;
        }
        return (
            <Scrollable>
                {templates.map(template => {
                    const { portfolioAnalysisTemplateId, templateName, metaData, createdBy, createdDate, lastAskedBy, lastAskedDate } = template;
                    const { parentDatasetId, question, fieldLabel, filters: { documentNameIds, entityIds, includePreExecution, date, isExclusiveEntityFilter } } = metaData;
                    const agreementType = getSelectedDataset(parentDatasetId!);
                    const documentNames = getDocumentNames(parentDatasetId!, documentNameIds);
                    const entityFiltersApplied = !isUndefined(entityIds) ? entityFilter(entityIds) : null;
                    const preExecutionFilterApplied = preExecutionFilter(includePreExecution);
                    const dateFilterApplied = !isUndefined(date) && !isNull(date.from) && !isNull(date.to);
                    const hasFilters = entityFiltersApplied || preExecutionFilterApplied || dateFilterApplied;
                    const fromDate = dateFilterApplied ? formatDate(date!.from!) : '';
                    const toDate = dateFilterApplied ? formatDate(date!.to!) : '';
                    return (
                        <div className={styles.template} key={portfolioAnalysisTemplateId}>
                            <OverflowTooltip className={styles.templateName} overlayText={templateName} />
                            <div className={styles.templateBody}>
                                <div className={styles.templateInfoWrapper}>
                                    <div className={styles.surveyDetailsWrapper} style={{ maxWidth: hasFilters ? '60%' : '100%' }}>
                                        <div className={styles.templateDetailWrapper}>
                                            <div className={styles.templateDetailLabel}>Survey Information:</div>
                                            <div className={styles.detailsWrapper}>
                                                <div className={styles.templateDetail}>{agreementType}</div>
                                                <TooltipList overlayText={documentNames} placement='bottom'>
                                                    <div className={styles.templateDetail}>{documentNames.join(', ')}</div>
                                                </TooltipList>
                                                <div className={styles.templateDetail}>{fieldLabel}</div>
                                            </div>
                                        </div>
                                        <div className={styles.templateDetailWrapper}>
                                            <div className={styles.templateDetailLabel}>Question Asked:</div>
                                            <div className={styles.detailsWrapper}>
                                                <OverflowTooltip className={styles.templateDetail} overlayText={question} />
                                            </div>
                                        </div>
                                        <div className={styles.templateDetailWrapper}>
                                            <div className={styles.templateDetailLabel}>Created By:</div>
                                            <div className={styles.detailsWrapper}>
                                                <div className={styles.templateDetail}>{`${createdBy} on ${formatDate(createdDate)}`}</div>
                                            </div>
                                        </div>
                                        <div className={styles.templateDetailWrapper}>
                                            <div className={styles.templateDetailLabel}>Last Asked By:</div>
                                            <div className={styles.detailsWrapper}>
                                                <div className={styles.templateDetail}>{`${lastAskedBy} on ${formatDate(lastAskedDate)}`}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.filtersWrapper} style={{ width: hasFilters ? 'calc(40% - 10px)' : 'none' }}>
                                        {hasFilters &&
                                            <>
                                                <div className={styles.filtersLabel}>Filters:</div>
                                                <div className={styles.appliedFilterWrapper}>
                                                    {entityFiltersApplied &&
                                                        <TooltipList overlayText={entityFiltersApplied} placement='bottom'>
                                                            <div className={styles.entities}>{entityFiltersApplied.join(', ')}</div>
                                                        </TooltipList>
                                                    }
                                                    {isExclusiveEntityFilter &&
                                                        <div className={styles.preExecution}>Include Exclusive Entities</div>
                                                    }
                                                    {preExecutionFilterApplied &&
                                                        <div className={styles.preExecution}>{preExecutionFilterApplied}</div>
                                                    }
                                                    {dateFilterApplied &&
                                                        <div className={styles.dateWrapper}>
                                                            <div className={styles.dateRangeWrapper}>
                                                                <div className={styles.dateRange}>
                                                                    <div className={styles.dateRangeLabel}>Executed Date From:</div>
                                                                    <div className={styles.dateRangeValue}>{fromDate}</div>
                                                                </div>
                                                                <div className={styles.dateRange}>
                                                                    <div className={styles.dateRangeLabel}>To:</div>
                                                                    <div className={styles.dateRangeValue}>{toDate}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            </>
                                        }
                                    </div>
                                </div>

                                <div className={styles.buttonsWrapper}>
                                    <Button label='Delete' onClick={() => openDeleteTemplateModal(portfolioAnalysisTemplateId)} />
                                    <div className={styles.groupedButtons}>
                                        <Button label='Edit' onClick={() => editTemplate(template)} />
                                        <Button label='Ask Again' onClick={() => askAgain(template)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </Scrollable>
        );
    }, [askAgain, editTemplate, entityFilter, getDocumentNames, getSelectedDataset, isFetchingTemplates, openDeleteTemplateModal, preExecutionFilter, templates]);

    return (
        <div className={styles.templatesPageWrapper}>
            <div className={styles.templatesHeaderWrapper}>
                <div className={styles.headerTitle}>Portfolio Analytics Templates</div>
                <div className={styles.changeViewWrapper} onClick={changeView}>
                    <div className={styles.label}>← Back</div>
                </div>
            </div>
            <div className={styles.templateWrapper}>
                {body}
            </div>
            <PortfolioAnalyticsTemplateDeleteModal templateIdToDelete={templateIdToDelete} setTemplateIdToDelete={setTemplateIdToDelete} />
        </div>
    );
};
